import { SwitchCase } from '@toss/react';
import { useTranslation } from 'next-i18next';
import { type Item } from '@/api';
import { QuestionmarkSmallBubbleColorIcon } from '@/icons';
import { AnswerIconO, AnswerIconX } from '@/shared/components/AnswerIcon';
import { prepareTiptapHTML, StaticRichTextRenderer } from '@/shared/features/rich-text';
import { useIsOXLocale } from '@/shared/hooks';
import { CHOICE_NUMBER_TEXT } from './const';
import { QuizWorksheetImage } from './QuizWorksheetImage';
import { type AnswerPageBlockData, type HintBlockData, type MatchAnswerBlockData, type MatchChoiceBlockData, type MultipleChoiceAnswerBlockData, type MultipleChoiceBlockData, type OxChoiceAnswerBlockData, type OxChoiceBlockData, type QuestionBlockData, type QuestionPageBlockData, type SubjectiveAnswerBlockData } from './type';
export const QUIZ_WORKSHEET_OX_ICON_CLASS_NAME = 'quiz-worksheet-ox-icon';
const getOXAriaLabel = (data: 'o' | 'x', isOXLocale: boolean) => {
  if (data === 'o') return isOXLocale ? 'O' : 'True';
  return isOXLocale ? 'X' : 'False';
};
const QuestionBlock = ({
  data
}: {
  data: QuestionBlockData['data'];
}) => {
  const {
    questionNumber,
    question,
    questionRichText,
    questionImageUrl
  } = data;
  return <div className="w-full" data-sentry-component="QuestionBlock" data-sentry-source-file="QuizWorksheetBlocks.tsx">
      <div className="flex flex-row gap-3xs text-[8px] font-medium leading-[15.4px] text-default">
        <span>{`${questionNumber}.`}</span>
        <StaticRichTextRenderer as="div" text={questionRichText || question} data-sentry-element="StaticRichTextRenderer" data-sentry-source-file="QuizWorksheetBlocks.tsx" />
      </div>
      {questionImageUrl && <div className="px-sm py-3xs">
          <QuizWorksheetImage imageUrl={questionImageUrl} alt={question} />
        </div>}
    </div>;
};
const IndividualMultipleChoiceBlock = ({
  data
}: {
  data: MultipleChoiceBlockData['data'];
}) => {
  const {
    choice,
    choiceNumber
  } = data;
  return <div className="pl-sm" data-sentry-component="IndividualMultipleChoiceBlock" data-sentry-source-file="QuizWorksheetBlocks.tsx">
      <div className="flex flex-row gap-3xs break-all !text-[8px] font-regular leading-[15.4px] text-neutral">
        <span className="text-caption-md-medium">
          {CHOICE_NUMBER_TEXT[`${choiceNumber}`]}
        </span>
        <StaticRichTextRenderer as="div" text={choice.richText ?? choice.text} data-sentry-element="StaticRichTextRenderer" data-sentry-source-file="QuizWorksheetBlocks.tsx" />
      </div>
      {choice.imageUrl && <div className="py-4xs pl-md pr-sm">
          <QuizWorksheetImage imageUrl={choice.imageUrl} alt={choice.text} />
        </div>}
    </div>;
};
const IndividualOXChoiceBlock = ({
  data
}: {
  data: OxChoiceBlockData['data'];
}) => {
  const isOXLocale = useIsOXLocale();
  const ariaLabel = getOXAriaLabel(data, isOXLocale);
  return <div className="pl-sm" data-sentry-component="IndividualOXChoiceBlock" data-sentry-source-file="QuizWorksheetBlocks.tsx">
      <div className="flex flex-row items-center gap-3xs text-caption-md-regular text-neutral">
        {data === 'o' ? <>
            <span>{CHOICE_NUMBER_TEXT['1']}</span>
            <span role="img" aria-label={ariaLabel} className={QUIZ_WORKSHEET_OX_ICON_CLASS_NAME}>
              <AnswerIconO className="size-sm" aria-hidden="true" />
            </span>
          </> : <>
            <span>{CHOICE_NUMBER_TEXT['2']}</span>
            <span role="img" aria-label={ariaLabel} className={QUIZ_WORKSHEET_OX_ICON_CLASS_NAME}>
              <AnswerIconX className="size-sm" aria-hidden="true" />
            </span>
          </>}
      </div>
    </div>;
};
const SubjectiveAnswerWriteBlock = () => {
  return <div className="flex flex-row justify-between pl-sm text-caption-md-regular text-neutral" data-sentry-component="SubjectiveAnswerWriteBlock" data-sentry-source-file="QuizWorksheetBlocks.tsx">
      <span>{'('}</span>
      <span>{')'}</span>
    </div>;
};
const EssayAnswerWriteBlock = () => {
  return <div className="flex flex-row justify-between pl-sm text-caption-md-regular text-neutral" data-sentry-component="EssayAnswerWriteBlock" data-sentry-source-file="QuizWorksheetBlocks.tsx">
      <span>{'('}</span>
      <span>{')'}</span>
    </div>;
};
const MatchItemContent = ({
  item
}: {
  item: Item;
}) => {
  const content = item.richText || (item.equation ? prepareTiptapHTML(`$${item.equation}$`) : item.text);
  return <div className="flex min-w-0 flex-1 flex-col gap-4xs" data-sentry-component="MatchItemContent" data-sentry-source-file="QuizWorksheetBlocks.tsx">
      {content && <StaticRichTextRenderer as="div" text={content} className="break-all" />}
      {item.imageUrl && <div className="py-4xs">
          <QuizWorksheetImage imageUrl={item.imageUrl} alt={item.text} />
        </div>}
    </div>;
};
type MatchAnswerPairWithItems = MatchAnswerBlockData['data']['answerPairs'][number] & {
  leftItem?: Item;
  rightItem?: Item;
};
const MatchChoiceBlock = ({
  data
}: {
  data: MatchChoiceBlockData['data'];
}) => {
  const {
    leftItems,
    rightItems
  } = data;
  const renderItem = (item: Item, side: 'left' | 'right') => <div key={item.id} className="relative flex min-w-0 flex-1 items-center gap-3xs rounded-2xs border border-neutral p-3xs !text-[8px] font-regular leading-[15.4px]" data-sentry-component="renderItem" data-sentry-source-file="QuizWorksheetBlocks.tsx">
      <MatchItemContent item={item} data-sentry-element="MatchItemContent" data-sentry-source-file="QuizWorksheetBlocks.tsx" />
      <span aria-hidden="true" className={`absolute top-1/2 size-[4px] -translate-y-1/2 rounded-full bg-current text-assistive ${side === 'left' ? '-right-[7.5px]' : '-left-[7.5px]'}`} />
    </div>;
  return <div className="grid grid-cols-2 items-start gap-md text-neutral" data-sentry-component="MatchChoiceBlock" data-sentry-source-file="QuizWorksheetBlocks.tsx">
      <div className="flex flex-col gap-2xs self-stretch pl-sm pr-xs">
        {leftItems.map(item => renderItem(item, 'left'))}
      </div>
      <div className="flex flex-col gap-2xs pl-sm">
        {rightItems.map(item => renderItem(item, 'right'))}
      </div>
    </div>;
};
const HintBlock = ({
  data
}: {
  data: HintBlockData['data'];
}) => {
  const {
    t
  } = useTranslation(['common']);
  return <div className="w-full pt-3xs" data-sentry-component="HintBlock" data-sentry-source-file="QuizWorksheetBlocks.tsx">
      <div className="flex w-full items-center gap-3xs rounded-2xs bg-opacity-black-2 px-sm py-4xs">
        <div className="flex items-center gap-3xs">
          <QuestionmarkSmallBubbleColorIcon size="2xs" data-sentry-element="QuestionmarkSmallBubbleColorIcon" data-sentry-source-file="QuizWorksheetBlocks.tsx" />
          <span className="!text-[8px] font-semibold leading-[15.4px] text-default">
            {t('quizItem.hint.title')}
          </span>
        </div>
        <span className="min-w-0 flex-1 !text-[8px] font-regular leading-[15.4px] text-neutral">
          {data.value}
        </span>
      </div>
    </div>;
};
export const QuestionPageBlockRenderer = ({
  props
}: {
  props: QuestionPageBlockData;
}) => {
  const {
    type,
    data
  } = props;
  return <SwitchCase value={type} caseBy={{
    question: <QuestionBlock data={data as QuestionBlockData['data']} />,
    multipleChoice: <IndividualMultipleChoiceBlock data={data as MultipleChoiceBlockData['data']} />,
    oxChoice: <IndividualOXChoiceBlock data={data as OxChoiceBlockData['data']} />,
    subjective: <SubjectiveAnswerWriteBlock />,
    essay: <EssayAnswerWriteBlock />,
    matchChoice: <MatchChoiceBlock data={data as MatchChoiceBlockData['data']} />,
    hint: <HintBlock data={data as HintBlockData['data']} />
  }} data-sentry-element="SwitchCase" data-sentry-component="QuestionPageBlockRenderer" data-sentry-source-file="QuizWorksheetBlocks.tsx" />;
};
const AnswerLabel = ({
  suffix = ''
}: {
  suffix?: string;
}) => {
  const {
    t
  } = useTranslation(['common']);
  return <span className="shrink-0 text-[8px] font-[500] leading-[15.4px] text-neutral" data-sentry-component="AnswerLabel" data-sentry-source-file="QuizWorksheetBlocks.tsx">
      {t('common:common.answer')}
      {suffix}
    </span>;
};
const MultipleQuestionAnswer = ({
  data
}: {
  data: MultipleChoiceAnswerBlockData['data'];
}) => {
  const {
    answerNumberList
  } = data;
  return <div className="flex flex-row items-center gap-3xs pl-sm" data-sentry-component="MultipleQuestionAnswer" data-sentry-source-file="QuizWorksheetBlocks.tsx">
      <AnswerLabel data-sentry-element="AnswerLabel" data-sentry-source-file="QuizWorksheetBlocks.tsx" />
      <span className="text-caption-md-regular text-neutral">
        {answerNumberList.map(answer => CHOICE_NUMBER_TEXT[`${Number(answer) + 1}`]).join(', ')}
      </span>
    </div>;
};
const OXQuestionAnswer = ({
  data
}: {
  data: OxChoiceAnswerBlockData['data'];
}) => {
  const isOXLocale = useIsOXLocale();
  const ariaLabel = getOXAriaLabel(data, isOXLocale);
  return <div className="flex flex-row items-center gap-3xs pl-sm" data-sentry-component="OXQuestionAnswer" data-sentry-source-file="QuizWorksheetBlocks.tsx">
      <AnswerLabel data-sentry-element="AnswerLabel" data-sentry-source-file="QuizWorksheetBlocks.tsx" />
      {data === 'o' ? <span role="img" aria-label={ariaLabel} className={QUIZ_WORKSHEET_OX_ICON_CLASS_NAME}>
          <AnswerIconO className="size-sm" aria-hidden="true" />
        </span> : <span role="img" aria-label={ariaLabel} className={QUIZ_WORKSHEET_OX_ICON_CLASS_NAME}>
          <AnswerIconX className="size-sm" aria-hidden="true" />
        </span>}
    </div>;
};
const SubjectiveQuestionAnswer = ({
  data
}: {
  data: SubjectiveAnswerBlockData['data'];
}) => {
  const {
    answerList
  } = data;
  return <div className="flex flex-row items-center gap-3xs pl-sm" data-sentry-component="SubjectiveQuestionAnswer" data-sentry-source-file="QuizWorksheetBlocks.tsx">
      <AnswerLabel data-sentry-element="AnswerLabel" data-sentry-source-file="QuizWorksheetBlocks.tsx" />
      <span className="break-all text-[8px] font-regular leading-[15.4px] text-neutral">
        {answerList.join(', ')}
      </span>
    </div>;
};
const MatchQuestionAnswer = ({
  data
}: {
  data: MatchAnswerBlockData['data'];
}) => {
  const answerPairs = data.answerPairs as MatchAnswerPairWithItems[];
  const renderItem = (item: Item | undefined, fallbackLabel: string) => {
    if (!item) {
      return <StaticRichTextRenderer as="span" text={fallbackLabel} className="min-w-0 break-all" />;
    }
    const content = item.richText || (item.equation ? prepareTiptapHTML(`$${item.equation}$`) : item.text);
    return <span className="flex min-w-0 flex-1 items-center gap-2xs" data-sentry-component="renderItem" data-sentry-source-file="QuizWorksheetBlocks.tsx">
        <StaticRichTextRenderer as="span" text={content} className="min-w-0 break-all" data-sentry-element="StaticRichTextRenderer" data-sentry-source-file="QuizWorksheetBlocks.tsx" />
        {item.imageUrl && <span className="shrink-0 py-4xs">
            <QuizWorksheetImage imageUrl={item.imageUrl} alt={item.text} />
          </span>}
      </span>;
  };
  return <div className="flex flex-row items-center gap-3xs pl-sm" data-sentry-component="MatchQuestionAnswer" data-sentry-source-file="QuizWorksheetBlocks.tsx">
      <AnswerLabel suffix=" :" data-sentry-element="AnswerLabel" data-sentry-source-file="QuizWorksheetBlocks.tsx" />
      <div className="flex min-w-0 flex-1 flex-col">
        {answerPairs.map(pair => <div key={`${pair.leftId}-${pair.rightId}`} className="flex min-w-0 flex-nowrap items-center gap-3xs !text-[8px] font-regular leading-[15.4px] text-neutral">
            {renderItem(pair.leftItem, pair.leftLabel)}
            <span aria-hidden="true" className="shrink-0">
              {'→'}
            </span>
            {renderItem(pair.rightItem, pair.rightLabel)}
          </div>)}
      </div>
    </div>;
};
export const AnswerPageBlockRenderer = ({
  props
}: {
  props: AnswerPageBlockData;
}) => {
  const {
    type,
    data
  } = props;
  return <SwitchCase value={type} caseBy={{
    question: <QuestionBlock data={data as QuestionBlockData['data']} />,
    multipleChoiceAnswer: <MultipleQuestionAnswer data={data as MultipleChoiceAnswerBlockData['data']} />,
    oxChoiceAnswer: <OXQuestionAnswer data={data as OxChoiceAnswerBlockData['data']} />,
    subjectiveAnswer: <SubjectiveQuestionAnswer data={data as SubjectiveAnswerBlockData['data']} />,
    matchAnswer: <MatchQuestionAnswer data={data as MatchAnswerBlockData['data']} />,
    essayAnswer: <div className="flex flex-row items-center gap-3xs pl-sm">
            <AnswerLabel />
          </div>
  }} data-sentry-element="SwitchCase" data-sentry-component="AnswerPageBlockRenderer" data-sentry-source-file="QuizWorksheetBlocks.tsx" />;
};